import { useEffect, useRef, useState } from 'react';
import { int, isoDay, magnitude, pct, USD } from '../lib/format';
import { C, HEADER_HEIGHT, ink, surface, text } from '../lib/theme';
import type { Filters, Reason } from '../lib/types';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useEventList } from '../lib/useEventList';
import { useFilters } from '../lib/useFilters';
import { EventList } from '../components/EventList';
import { EmptyState, SectionHeader } from '../components/primitives';
import type { EventCardHandlers } from '../components/EventCard';
import { AuditadosCard } from '../components/AuditadosCard';
import { ConfirmadosPanel } from '../components/ConfirmadosPanel';
import { FalsePositiveButton } from '../components/FalsePositiveButton';
import type { Origin } from '../components/ScaleModal';
import { IconShieldCheck } from '../components/icons';
import { useToast } from '../components/Toast';

/**
 * Aba Fila (PRD §4) — a tela de trabalho do dia. Só `audit_status = em_analise`.
 * Índice local: 01 Resumo da fila (faixa minimalista) · 02 Fila (55%) + Auditados (card lateral).
 */
export function FilaPage({ filters, reasons, handlers }: {
  filters: Filters;
  reasons: Reason[];
  handlers: Omit<EventCardHandlers, 'onAudit' | 'onPickReason' | 'onSetCorrection' | 'onClearCorrection'>;
}) {
  const toast = useToast();
  const { setRange } = useFilters();
  const [confirmadosOrigin, setConfirmadosOrigin] = useState<Origin | null>(null);

  // Fila começa no mês vigente por padrão, não no histórico inteiro — pedido
  // explícito ("oculte esses pendentes de fila de agosto... trabalhar de
  // forma mais organizada a partir daqui"). Mesma leitura inteligente já
  // usada em Clientes/Visão Geral: só roda uma vez na entrada da página, e só
  // se o período ainda não foi tocado — limpar o filtro manualmente depois
  // não deve ser sobrescrito. Pendências antigas continuam acessíveis, só não
  // são mais o padrão — o seletor de período abre pra "Tudo" quando precisar.
  const didDefaultPeriod = useRef(false);
  useEffect(() => {
    if (didDefaultPeriod.current) return;
    didDefaultPeriod.current = true;
    if (!filters.from && !filters.to) {
      const now = new Date();
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setRange(isoDay(first), isoDay(last));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Um único fetch leve pro resumo — não precisa mais dos 4 KPIs cheios nem
  // do sparkline, que saíram junto com os KpiCards.
  const visao = useAsync(() => api.visaoGeralKpis(filters), [JSON.stringify(filters)]);
  const list = useEventList(filters, 'fila', reasons);

  const v = visao.data;

  const fullHandlers: EventCardHandlers = {
    ...handlers,
    onAudit: (ev) => list.toggleAudit(ev, (msg) => toast(msg, 'error')),
    onPickReason: (ev, picked) => {
      const reason = picked[0] ?? null;
      list.setReason(ev, picked, (msg) => toast(msg, 'error'));
      if (reason?.is_exception) {
        toast('Quando esse evento for confirmado como Real Lost, ele não vai entrar no total de Perda Confirmada.', 'info');
      }
    },
    onSetCorrection: (ev, body) => list.setCorrection(ev, body, (msg) => toast(msg, 'error')),
    onClearCorrection: (ev) => list.clearCorrection(ev, (msg) => toast(msg, 'error')),
  };

  const overview = [
    {
      label: 'Pendentes', color: C.accent,
      value: visao.loading ? '—' : String(int(v?.fila_pendente_count)),
      sub: visao.loading ? '' : USD(magnitude(v?.fila_pendente_valor)),
      subEmphasis: false,
    },
    {
      label: 'Concluídos', color: C.green,
      value: visao.loading ? '—' : String(int(v?.confirmados_count)),
      sub: visao.loading ? '' : USD(magnitude(v?.perda_confirmada)),
      subEmphasis: false,
    },
    {
      label: 'Cobertura', color: C.blueSoft,
      value: visao.loading ? '—' : pct(v?.cobertura_pct ?? 0),
      sub: 'da fila já auditada',
      subEmphasis: false,
    },
    // VDI (Índice de Inconsistência) — pedido explícito de ficar visível
    // aqui no resumo, não só dentro do painel de Confirmados: motivo de
    // regra sinalizado mas ainda SEM correção manual. `C.blue` (não
    // `C.blueSoft`, que já é a cor da "Cobertura" ao lado — precisa de tom
    // diferente pra não confundir os dois tiles azuis lado a lado nesta
    // fileira específica).
    //
    // Texto do subtítulo trocado (pedido explícito, 2026-09-10): "pendente
    // de revisão" soava como alarme/pendência urgente; o Rodrigo quer o
    // enquadramento oposto — já foi avaliado, só não é perda de verdade.
    {
      label: 'VDI', color: C.blue,
      value: visao.loading ? '—' : String(int(v?.vdi_count)),
      sub: visao.loading ? '' : `${USD(magnitude(v?.vdi_valor))} Avaliados sem perca`,
      subEmphasis: true,
    },
  ];

  return (
    <>
      <section style={{ marginBottom: 30 }}>
        <SectionHeader id="resumo" title="Resumo da fila" />
        <div style={{ ...surface, display: 'flex', padding: '17px 6px' }}>
          {overview.map((seg, i) => (
            <div key={seg.label} style={{
              flex: 1, minWidth: 0, padding: '0 22px', display: 'flex', flexDirection: 'column', gap: 5,
              borderLeft: i > 0 ? `1px solid ${ink(0.08)}` : 'none',
            }}>
              <span style={{
                fontSize: 10.5, fontWeight: 800, letterSpacing: '0.13em', textTransform: 'uppercase', color: text.title,
              }}>{seg.label}</span>
              <span style={{ display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap' }}>
                <span style={{
                  fontSize: 25, fontWeight: 800, letterSpacing: '-0.025em', fontVariantNumeric: 'tabular-nums',
                  color: seg.color,
                }}>{seg.value}</span>
                {/* Branco (pedido explícito, 2026-09-10) em vez de `ink(0.42)`
                    apagado — o subtítulo carrega dado real (valor em R$, não
                    só um rótulo decorativo), precisa de contraste igual ao
                    resto. O do VDI ganha destaque a mais (`subEmphasis`):
                    peso maior, sem opacidade reduzida nenhuma — é a leitura
                    mais nova e a que o Rodrigo quer que salte primeiro. */}
                {seg.sub && (
                  <span style={{
                    fontSize: seg.subEmphasis ? 13.5 : 12,
                    fontWeight: seg.subEmphasis ? 750 : 600,
                    color: seg.subEmphasis ? C.fg : ink(0.78),
                  }}>{seg.sub}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Fila (55%) + Auditados (card lateral, retangular vertical) — o
          detalhe completo dos confirmados continua a um clique, no painel. */}
      {/* `minmax(0,...)` nas duas colunas, não `55% 1fr`: no grid, o mínimo
          default de um item é `auto` (= largura intrínseca do conteúdo), então
          a coluna dos Auditados esticava além do 1fr pra caber os botões da
          linha de evento e empurrava a página inteira pra direita, criando
          scroll horizontal. Com minmax(0,...) a coluna respeita a fração e o
          conteúdo trunca dentro dela, como já era a intenção. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,55fr) minmax(0,45fr)', gap: 22, alignItems: 'stretch' }}>
        <section style={{ minWidth: 0 }}>
          <SectionHeader id="lista" title="Lista de eventos" />
          <EventList
            items={list.items} loading={list.loading} loadingMore={list.loadingMore}
            error={list.error} hasMore={list.hasMore} busyIds={list.busyIds}
            variant="fila" reasons={reasons} filters={filters} handlers={fullHandlers}
            onLoadMore={list.loadMore} onRetry={list.reload}
            empty={
              <EmptyState
                tone="good" icon={<IconShieldCheck size={22} />} title="Tudo auditado por aqui"
                text="Nenhum evento em análise no período. O que já foi validado está no cartão de Auditados, ao lado."
              />
            }
          />
        </section>

        {/* `sticky` + `alignSelf: start`: sem isso o grid estica esta coluna
            pra acompanhar a altura da lista da esquerda (que pode ter
            centenas de itens) — um cartão de milhares de pixels de alto,
            quase todo vazio. Fixo na tela em vez de crescer junto. */}
        <section style={{
          position: 'sticky', top: HEADER_HEIGHT + 52, alignSelf: 'start',
          display: 'flex', flexDirection: 'column', minWidth: 0,
        }}>
          <SectionHeader id="auditados" title="Auditados" />
          <AuditadosCard
            filters={filters} reasons={reasons} handlers={handlers}
            onOpenPanel={setConfirmadosOrigin}
          />
        </section>
      </div>

      <ConfirmadosPanel
        open={confirmadosOrigin !== null} origin={confirmadosOrigin}
        onClose={() => setConfirmadosOrigin(null)}
        filters={filters} reasons={reasons} handlers={handlers}
      />

      <FalsePositiveButton filters={filters} />
    </>
  );
}
